import { useIsFocused, useNavigation } from '@react-navigation/native';
import { Formik } from 'formik';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Platform,
  ScrollView,
  StatusBar,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  KeyboardAvoidingView,
} from 'react-native';
import FastImage from 'react-native-fast-image';
import LinearGradient from 'react-native-linear-gradient';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import * as yup from 'yup';
import { PyxiLabel } from '~assets/icons';
import ModernButton from '~components/ModernButton';
import ModernTextInput from '~components/ModernTextInput';
import ModernCard from '~components/ModernCard/ModernCard';
import SocialButtons from '~components/SocialButtons';
import { SCREENS } from '~constants';
import { spacing, borderRadius, typography, shadows } from '~constants/design';
import { jwtDecode } from 'jwt-decode';
import { NavigationProps } from '~types/navigation/navigation.type';
import { logScreenView, logEvent, setUserId } from '~Utils/firebaseAnalytics';
import { LanguagesModal } from '~components/ModalWithItems/LanguagesModal';
import { useLanguage } from '~containers/Core/navigation/SettingsStack/zustand';
import { useTheme } from '~contexts/ThemeContext';
import { FadeIn } from '~components/MicroInteractions';
import ModernSpinner from '~components/ModernSpinner';
import { useGetUserType } from '~hooks/event/useGetUserType';
import { useGetUserAccount } from '~hooks/user/useGetUser';
import { useGetBusinessAccount } from '~hooks/business/useGetBusinessAccount';
import { useEnhancedAuth } from '~hooks/auth/useEnhancedAuth';
import { useUserStore } from '~providers/userStore/zustand';
import { useLogin } from '~hooks/auth/useLogin';
import { useGetUserById } from '~hooks/user/useGetUserById';
import { BASE_API_URL } from '@env';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '~constants';

// Use web-safe wrapper for KeyboardAwareScrollView
const KeyboardScrollComponent = Platform.OS === 'web' ? ScrollView : KeyboardAwareScrollView;

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Enter a valid email')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .min(1, 'Password is required')
    .required('Password is required'),
});

const LoginView = () => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();
  const navigation = useNavigation<NavigationProps>();
  const { bottom } = useSafeAreaInsets();
  const isAndroid = Platform.OS === 'android';

  const [isLoading, setIsLoading] = useState(false);
  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false);
  const [isPostLoginLoading, setIsPostLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  
  // State to control form values
  const [formValues, setFormValues] = useState({ email: '', password: '' });

  // Clear error function to prevent maximum update depth
  const clearErrorIfExists = useCallback(() => {
    setLoginError(prev => (prev ? null : prev));
  }, []);
  const isFocused = useIsFocused();
  const { language } = useLanguage();
  const { setUser, setAccessToken } = useUserStore();
  const [fetchedUserId, setFetchedUserId] = useState<string | undefined>(undefined);
  const [fetchedToken, setFetchedToken] = useState<string | undefined>(undefined);
  // Use Zustand for user and token
  const user = useUserStore(state => state.user);
  const token = useUserStore(state => state.token);
  const { mutateAsync: signInApi, isLoading: signInLoading } = useLogin();

  // Get current user ID from Zustand
  const userId = user?.uid || '';
  const isAuthenticated = !!token;

  // Fallback to original hooks for compatibility
  const { data: userType, isLoading: userTypeIsLoading } = useGetUserType(userId);
  const { data: userData, isLoading: userAccountIsLoading } = useGetUserAccount(userId);
  const { data: businessData, isLoading: businessIsLoading } = useGetBusinessAccount(userId);

  useEffect(() => {
    setIsLoading(false);
  }, [isFocused]);

  // Clear password field when user navigates away from login screen
  useEffect(() => {
    if (!isFocused) {
      // Clear the password field when screen loses focus, but keep email
      setFormValues(prev => ({ ...prev, password: '' }));
    }
  }, [isFocused]);

  // Remove Firebase Auth state change effect

  useEffect(() => {
    logScreenView('Login', 'LoginView');
  }, []);

  const paddingBottom = isAndroid ? bottom + spacing.lg : bottom;

  const styles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContainer: {
      flexGrow: 1,
      minHeight: height - (StatusBar.currentHeight || 0),
      paddingBottom: spacing.xl, // Extra padding for keyboard scenarios
    },
    heroSection: {
      height: height * 0.28, // Further reduced to ensure register button is visible
      position: 'relative' as const,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      overflow: 'hidden' as const,
    },
    heroGradient: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    heroContent: {
      flex: 1,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      zIndex: 2,
      paddingHorizontal: spacing.lg,
    },
    decorativeCircle1: {
      position: 'absolute' as const,
      top: -50,
      right: -50,
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: colors.textInverse + '15',
    },
    decorativeCircle2: {
      position: 'absolute' as const,
      bottom: -30,
      left: -30,
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.textInverse + '10',
    },
    decorativeCircle3: {
      position: 'absolute' as const,
      top: 120,
      left: -20,
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: colors.textInverse + '20',
    },
    floatingElements: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    floatingDot: {
      position: 'absolute' as const,
      width: 8,
      height: 8,
      borderRadius: 4,
      top: 40,
      right: 60,
    },
    languageButton: {
      position: 'absolute' as const,
      top: spacing.md,
      right: spacing.md,
      backgroundColor: colors.textInverse + '10',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.full,
      borderWidth: 1,
      borderColor: colors.textInverse + '20',
      zIndex: 3,
      minWidth: 44,
      height: 32,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      backdropFilter: 'blur(10px)',
    },
    languageText: {
      color: colors.textInverse,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      letterSpacing: 0.3,
      opacity: 0.9,
    },
    logoContainer: {
      alignItems: 'center' as const,
      marginBottom: spacing.lg,
      transform: [{ scale: 1.1 }], // Slightly bigger but not too much
    },
    welcomeText: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.black,
      color: colors.textInverse,
      textAlign: 'center' as const,
      marginTop: spacing.sm,
      letterSpacing: -0.5,
      // Removed text shadow for modern look
      lineHeight: typography.fontSize['3xl'] * 1.2,
    },
    subtitleText: {
      fontSize: typography.fontSize.lg,
      color: colors.textInverse,
      textAlign: 'center' as const,
      opacity: 0.95,
      marginTop: spacing.xs,
      fontWeight: typography.fontWeight.semibold,
      letterSpacing: 0.2,
      // Removed text shadow for modern look
    },
    subSubtitleText: {
      fontSize: typography.fontSize.sm,
      color: colors.textInverse,
      textAlign: 'center' as const,
      opacity: 0.85,
      marginTop: spacing.xs,
      fontWeight: typography.fontWeight.medium,
      letterSpacing: 0.1,
    },
    taglineText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.infoDark,
      textAlign: 'center' as const,
      marginTop: spacing.md,
      opacity: 0.95,
      letterSpacing: 0.5,
    },
    formSection: {
      paddingHorizontal: 0,
      paddingTop: spacing.lg,
    },
    formCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius['2xl'],
      padding: spacing.lg,
      ...shadows.lg,
      marginBottom: spacing.md,
    },
    formTitle: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center' as const,
      marginBottom: spacing.sm,
      letterSpacing: -0.3,
    },
    formSubtitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      textAlign: 'center' as const,
      marginBottom: spacing.lg,
      opacity: 0.85,
      letterSpacing: 0.1,
      lineHeight: typography.fontSize.base * 1.4,
    },
    inputContainer: {
      marginBottom: spacing.sm,
    },
    errorContainer: {
      backgroundColor: colors.error + '10',
      borderColor: colors.error + '30',
      borderWidth: 1,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
    },
    errorText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.error,
      textAlign: 'center' as const,
    },
    forgotPasswordContainer: {
      alignItems: 'flex-end' as const,
      marginBottom: spacing.md,
    },
    forgotPasswordText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
    },
    dividerContainer: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      marginVertical: spacing.md,
    },
    divider: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border,
    },
    dividerText: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginHorizontal: spacing.md,
      fontWeight: typography.fontWeight.medium,
    },
    bottomSection: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.lg,
      paddingBottom: Math.max(paddingBottom, spacing.lg),
      alignItems: 'center' as const,
      marginTop: spacing.md,
    },
    signupContainer: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    signupText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      fontWeight: typography.fontWeight.normal,
    },
    signupButton: {
      marginLeft: spacing.xs,
    },
    signupButtonText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.primary,
    },
    loadingOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.background + 'E6', // Semi-transparent background
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
      zIndex: 1000,
    },
    loadingContainer: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      ...shadows.lg,
      minWidth: 160,
    },
    loadingText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textPrimary,
      marginTop: spacing.md,
      textAlign: 'center' as const,
    },
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
        <KeyboardScrollComponent
          style={styles.keyboardContainer}
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          enableOnAndroid={true}
          enableAutomaticScroll={true}
          keyboardOpeningTime={0}
          extraScrollHeight={Platform.OS === 'ios' ? 20 : 40}
          extraHeight={Platform.OS === 'ios' ? 20 : 40}>
          {/* Hero Section */}
          <FadeIn duration={300}>
            <View style={styles.heroSection}>
              {/* Modern Gradient Background */}
              <LinearGradient
                colors={[colors.primary, '#FFB347', '#FFD700', '#FF7F50', colors.primaryLight]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.heroGradient}>
                {/* Decorative Elements */}
                <View style={styles.decorativeCircle1} />
                <View style={styles.decorativeCircle2} />
                <View style={styles.decorativeCircle3} />
              </LinearGradient>

              <TouchableOpacity style={styles.languageButton} onPress={() => setIsLanguagesModalVisible(true)}>
                <Text style={styles.languageText}>{language.toUpperCase()}</Text>
              </TouchableOpacity>

              <View style={styles.heroContent}>
                <FadeIn delay={100} duration={400}>
                  <View style={styles.logoContainer}>
                    <PyxiLabel />
                  </View>
                </FadeIn>

                <FadeIn delay={150} duration={400}>
                  <Text style={styles.taglineText}>{t('signin.discover_connect_experience')}</Text>
                </FadeIn>

                {/* Modern Floating Elements */}
                <View style={styles.floatingElements}>
                  <View style={[styles.floatingDot, { backgroundColor: colors.textInverse + '30' }]} />
                  <View
                    style={[styles.floatingDot, { backgroundColor: colors.textInverse + '20', top: 60, right: 40 }]}
                  />
                  <View
                    style={[styles.floatingDot, { backgroundColor: colors.textInverse + '25', bottom: 80, left: 30 }]}
                  />
                </View>
              </View>
            </View>
          </FadeIn>

          {/* Form Section */}
          <View style={styles.formSection}>
            <FadeIn delay={50} duration={300}>
              <ModernCard variant="default" padding="xl" style={{ marginBottom: spacing.lg, ...shadows.none }}>
                <Text style={styles.formTitle}>{t('signin.sign_in_title')}</Text>
                <Text style={styles.formSubtitle}>{t('signin.continue_event_journey')}</Text>

                {/* Error Display */}
                {loginError && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{loginError}</Text>
                  </View>
                )}

                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  onSubmit={async (values, actions) => {
                    setIsLoading(true);
                    setLoginError(null);
                    logEvent(ANALYTICS_EVENTS.LOGIN_STARTED, {
                      [ANALYTICS_PARAMS.AUTH_METHOD]: 'email',
                    });
                    try {
                      const result = await signInApi({
                        email: values.email,
                        password: values.password,
                      });
                      if (result.set_password_required) {
                        navigation.navigate(SCREENS.SET_PASSWORD, {
                          email: values.email,
                        });
                        setIsLoading(false);
                        return;
                      }
                      if (result.verified_required) {
                        navigation.navigate(SCREENS.OTP_VERIFICATION, {
                          email: values.email,
                          password: values.password,
                        });
                        setIsLoading(false);
                        return;
                      }
                      // JWT is in result.message
                      if (result.message) {
                        const token = result.message;
                        setAccessToken(token);
                        // Decode JWT and get user id
                        const jwt = token.replace(/^PYXI_/, '');
                        console.log(jwt, 'jwt');

                        const jwtPayload = jwtDecode(jwt);
                        console.log(jwtPayload, 'jwt');
                        const userId = jwtPayload.sub;

                        logEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED, {
                          [ANALYTICS_PARAMS.AUTH_METHOD]: 'email',
                          [ANALYTICS_PARAMS.CUSTOMER_ID]: userId,
                        });
                        setUserId(userId);

                        // Fetch full user from API
                        console.log(`${BASE_API_URL || ''}users/${userId}`, 'url');
                        console.log(token.startsWith('Bearer') ? token : `Bearer ${token}`, 'token');


                        const userResponse = await fetch(`${BASE_API_URL}users/${userId}`, {
                          method: 'GET',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: token,
                          },
                        });
                        if (!userResponse.ok) {
                          throw new Error('Failed to fetch user profile');
                        }
                        const fullUser = await userResponse.json();
                        setAccessToken(token)
                        setUser(fullUser);
                        // Optionally, trigger post-login loading
                       // setIsPostLoginLoading(true);
                      }
                    } catch (error: any) {
                      console.log(error);
                      logEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
                        [ANALYTICS_PARAMS.AUTH_METHOD]: 'email',
                        [ANALYTICS_PARAMS.ERROR_CODE]: error.message || 'unknown_error',
                      });

                      setLoginError(error.message || 'Login failed. Please try again.');
                    }
                    setIsLoading(false);
                  }}
                  validationSchema={validationSchema}
                  validateOnChange={true}
                  validateOnBlur={true}>
                  {formikProps => (
                    <>
                      <View style={styles.inputContainer}>
                        <ModernTextInput
                          label={t('signin.email')}
                          placeholder="Enter your email"
                          value={formikProps.values.email}
                          onChangeText={value => {
                            clearErrorIfExists();
                            const trimmedValue = value.trim();
                            formikProps.handleChange('email')(trimmedValue);
                            setFormValues(prev => ({ ...prev, email: trimmedValue }));
                          }}
                          errorText={formikProps.touched.email && formikProps.errors.email ? String(formikProps.errors.email) : undefined}
                          keyboardType="email-address"
                          autoCapitalize="none"
                          autoCorrect={false}
                          variant="outlined"
                          size="lg"
                        />
                      </View>

                      <View style={styles.inputContainer}>
                        <ModernTextInput
                          label={t('signin.password')}
                          placeholder="Enter your password"
                          value={formikProps.values.password}
                          onChangeText={value => {
                            clearErrorIfExists();
                            formikProps.handleChange('password')(value);
                            setFormValues(prev => ({ ...prev, password: value }));
                          }}
                          errorText={formikProps.touched.password && formikProps.errors.password ? String(formikProps.errors.password) : undefined}
                          isPassword
                          variant="outlined"
                          size="lg"
                        />
                      </View>

                      <View style={styles.forgotPasswordContainer}>
                        <TouchableOpacity
                          onPress={() => {
                            navigation.navigate(SCREENS.FORGOT_PASSWORD);
                          }}>
                          <Text style={styles.forgotPasswordText}>{t('signin.forgot_pwd_header') || 'Forgot Password?'}</Text>
                        </TouchableOpacity>
                      </View>

                      <ModernButton
                        onPress={() => formikProps.handleSubmit()}
                        title={t('signin.login')}
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isLoading || signInLoading}
                        disabled={!formikProps.isValid || isLoading || signInLoading}
                        hapticFeedback
                      />

                     {false && <View style={styles.dividerContainer}>
                        <View style={styles.divider} />
                        <Text style={styles.dividerText}>or continue with</Text>
                        <View style={styles.divider} />
                      </View>}

                      {false && <SocialButtons />}
                    </>
                  )}
                </Formik>
              </ModernCard>
            </FadeIn>
          </View>

          {/* Bottom Section */}
          <FadeIn delay={100} duration={300}>
            <View style={styles.bottomSection}>
              <View style={styles.signupContainer}>
                <Text style={styles.signupText}>{t('signin.no_acc_question')}</Text>
                <TouchableOpacity
                  style={styles.signupButton}
                  onPress={() => {
                    navigation.navigate(SCREENS.SIGN_UP);
                  }}>
                  <Text style={styles.signupButtonText}>{t('signin.register')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </FadeIn>
        </KeyboardScrollComponent>
      </KeyboardAvoidingView>

      <LanguagesModal isVisible={isLanguagesModalVisible} onClose={() => setIsLanguagesModalVisible(false)} />

      {/* Post-login loading overlay */}
      {isPostLoginLoading && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContainer}>
            <ModernSpinner size={40} variant="gradient" />
            <Text style={styles.loadingText}>{t('loading.fetching_data')}</Text>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
};

export default LoginView;
